import { Link } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import type { Brand } from "./brands";
import type { ArticleCard as CardVM } from "./reading";

/* ------------------------------------------------------------------ *
 * COMPONENTS.md — the recurring portal patterns, built once.
 * ------------------------------------------------------------------ */

// SourceBadge — provenance on every piece of content (never omitted).
export function SourceBadge({ source, brand }: { source: { label: string; tenant: boolean }; brand: Brand }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 2,
        padding: "2px 8px",
        whiteSpace: "nowrap",
        background: source.tenant ? "#f0efe9" : brand.soft,
        color: source.tenant ? "#4a504a" : brand.ink,
      }}
    >
      {source.label}
    </span>
  );
}

// VideoChip — marks a video article inside the unified corpus.
export function VideoChip() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#1d241f", color: "#fff", borderRadius: 999, padding: "2px 8px", fontSize: 10.5, fontWeight: 600 }}>
      <Icon name="play" size={10} color="#fff" />
      Video
    </span>
  );
}

export function SectionHeader({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.3, textTransform: "uppercase", color: "#8a8e88" }}>{children}</div>;
}

// ArticleCard — category · SourceBadge · VideoChip? · title · excerpt · meta.
export function ArticleCard({ card, brand }: { card: CardVM; brand: Brand }) {
  return (
    <Link
      to="/portal/article/$id"
      params={{ id: card.id }}
      style={{
        textDecoration: "none",
        color: "inherit",
        display: "block",
        background: "#fff",
        borderRadius: 3,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)",
        padding: "15px 16px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#8a8e88" }}>{card.cat}</span>
        <span style={{ flex: 1 }} />
        {card.isVideo && <VideoChip />}
        <SourceBadge source={card.source} brand={brand} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "#1d241f", lineHeight: 1.35, letterSpacing: "-0.2px" }}>{card.title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a504a", marginTop: 6, textWrap: "pretty" }}>{card.excerpt}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 11, fontSize: 11.5, color: "#8a8e88" }}>
        <span>{card.audience}</span>
        <span>·</span>
        <span>{card.kindLabel}</span>
        <span>·</span>
        <span>Updated {card.updated}</span>
        {card.gated && (
          <>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 10.5, fontWeight: 600, background: "#fbf2dc", color: "#9a6a12", borderRadius: 2, padding: "2px 7px" }}>Add-on</span>
          </>
        )}
      </div>
    </Link>
  );
}

// StepList — numbered circles + title/detail (articles and onboarding).
export function StepList({ steps, brand }: { steps: [string, string][]; brand: Brand }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {steps.map(([title, detail], i) => (
        <div key={i} style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
          <span style={{ flex: "none", width: 26, height: 26, borderRadius: 3, background: brand.soft, color: brand.ink, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{i + 1}</span>
          <div style={{ paddingTop: 2 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1d241f" }}>{title}</div>
            {detail && <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a504a", marginTop: 2, textWrap: "pretty" }}>{detail}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ProvenanceNote — plain-language "who wrote this / who can edit it".
export function ProvenanceNote({ tenant, orgName, brand }: { tenant: boolean; orgName: string; brand: Brand }) {
  return (
    <div style={{ display: "flex", gap: 11, alignItems: "flex-start", background: "#fafaf7", borderRadius: 3, boxShadow: "inset 0 0 0 1px #ececec", padding: "12px 14px" }}>
      <Icon name="book-open" size={16} color="#8a8e88" />
      <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "#4a504a", textWrap: "pretty" }}>
        {tenant ? (
          <>This is a <strong style={{ fontWeight: 600, color: "#1d241f" }}>{orgName}</strong> document — written and maintained by your organization, and visible only in your space.</>
        ) : (
          <>This is a <strong style={{ fontWeight: 600, color: "#1d241f" }}>{brand.name}</strong> platform document, maintained by the {brand.name} team and shared across every organization.</>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * ArticleBody — renders the ten block types (DATA_MODEL.md §1.2).
 * ------------------------------------------------------------------ */
type Block = unknown[];

export function ArticleBody({ blocks, brand }: { blocks: Block[]; brand: Brand }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {blocks.map((blk, i) => (
        <BlockView key={i} blk={blk} brand={brand} />
      ))}
    </div>
  );
}

function BlockView({ blk, brand }: { blk: Block; brand: Brand }) {
  const [kind, a, b, c] = blk as [string, unknown, unknown, unknown];
  switch (kind) {
    case "p":
      return <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.7, color: "#2d332e", textWrap: "pretty" }}>{a as string}</p>;
    case "h":
      return <h2 style={{ margin: "8px 0 0", fontSize: 18, fontWeight: 600, letterSpacing: "-0.4px", color: "#1d241f" }}>{a as string}</h2>;
    case "l":
      return (
        <ul style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 7 }}>
          {(a as string[]).map((it, i) => (
            <li key={i} style={{ fontSize: 14, lineHeight: 1.6, color: "#2d332e" }}>{it}</li>
          ))}
        </ul>
      );
    case "n":
      return (
        <div style={{ display: "flex", gap: 11, alignItems: "flex-start", background: brand.soft, borderRadius: 3, boxShadow: `inset 0 0 0 1px ${brand.line}`, padding: "13px 15px" }}>
          <Icon name="lightbulb" size={16} color={brand.ink} />
          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#2d332e", textWrap: "pretty" }}>{a as string}</div>
        </div>
      );
    case "img":
      return (
        <figure style={{ margin: 0 }}>
          <div style={{ background: "#f0efe9", borderRadius: 3, height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#bcbfba" }}>
            <Icon name="book" size={26} color="#bcbfba" />
          </div>
          {a ? <figcaption style={{ fontSize: 12, color: "#8a8e88", marginTop: 7 }}>{a as string}</figcaption> : null}
        </figure>
      );
    case "vid":
      // block: ["vid", title, duration, streamUid]
      return <VideoPlayer title={a as string} len={b as string} uid={c as string | undefined} />;
    case "steps":
      return <StepList steps={a as [string, string][]} brand={brand} />;
    case "chapters":
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {(a as [string, string][]).map(([time, title], i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "baseline", padding: "8px 10px", borderRadius: 3, background: i % 2 ? "#fafaf7" : "transparent" }}>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: brand.ink, minWidth: 44 }}>{time}</span>
              <span style={{ fontSize: 13.5, color: "#2d332e" }}>{title}</span>
            </div>
          ))}
        </div>
      );
    case "table": {
      const cols = a as string[];
      const rows = b as string[][];
      return (
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 420, border: "1px solid #f0efe9", borderRadius: 3 }}>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols.length}, minmax(120px, 1fr))`, background: "#fafaf7", borderBottom: "1px solid #f0efe9" }}>
              {cols.map((c2, i) => (
                <div key={i} style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8a8e88", padding: "9px 12px" }}>{c2}</div>
              ))}
            </div>
            {rows.map((r, ri) => (
              <div key={ri} style={{ display: "grid", gridTemplateColumns: `repeat(${cols.length}, minmax(120px, 1fr))`, borderTop: ri ? "1px solid #f4f3ee" : "none" }}>
                {r.map((cell, ci) => (
                  <div key={ci} style={{ fontSize: 13, color: "#2d332e", padding: "9px 12px" }}>{cell}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "code":
      return (
        <pre style={{ margin: 0, overflowX: "auto", background: "#1d241f", color: "#e7e1d4", borderRadius: 3, padding: "14px 16px", fontFamily: "'Space Mono', monospace", fontSize: 12.5, lineHeight: 1.6 }}>
          <code>{a as string}</code>
        </pre>
      );
    default:
      return null;
  }
  void c;
}

// BurnDown — cumulative remaining hours vs the approved plan, by week
// (COMPONENTS.md / DATA_MODEL.md §4: always computed, the approved line is the
// reference). `ideal`/`actual` are remaining-hours series; actual is partial.
export function BurnDown({ labels, ideal, actual, approved, brand }: { labels: string[]; ideal: number[]; actual: number[]; approved: number; brand: Brand }) {
  const W = 640;
  const H = 220;
  const padL = 40;
  const padB = 26;
  const padT = 12;
  const padR = 12;
  const n = labels.length;
  const maxY = Math.max(approved, ...ideal, ...actual, 1);
  const x = (i: number) => padL + (i / Math.max(1, n - 1)) * (W - padL - padR);
  const y = (v: number) => padT + (1 - v / maxY) * (H - padT - padB);
  const path = (arr: number[]) => arr.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const gridVals = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(maxY * f));

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 480, display: "block" }} role="img" aria-label="Burn-down of remaining hours against the approved plan">
        {gridVals.map((v) => (
          <g key={v}>
            <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke="#f0efe9" strokeWidth={1} />
            <text x={padL - 6} y={y(v) + 3} textAnchor="end" fontSize={9} fill="#bcbfba" fontFamily="'Space Mono', monospace">{v}</text>
          </g>
        ))}
        {labels.map((l, i) => (i % 2 === 0 || i === n - 1) && (
          <text key={l} x={x(i)} y={H - 8} textAnchor="middle" fontSize={9} fill="#8a8e88">{l}</text>
        ))}
        {/* approved plan (ideal) — dashed reference */}
        <path d={path(ideal)} fill="none" stroke="#bcbfba" strokeWidth={1.5} strokeDasharray="4 3" />
        {/* actual remaining — brand ink */}
        <path d={path(actual)} fill="none" stroke={brand.ink} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
        {actual.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r={2.5} fill={brand.ink} />)}
      </svg>
      <div style={{ display: "flex", gap: 16, fontSize: 11.5, color: "#8a8e88", paddingLeft: 40, marginTop: 2 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 14, height: 2, background: brand.ink, display: "inline-block" }} /> Hours remaining</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 14, height: 0, borderTop: "1.5px dashed #bcbfba", display: "inline-block" }} /> Approved plan</span>
      </div>
    </div>
  );
}

// VideoPlayer — the Cloudflare Stream player embedded by UID when the clip is
// uploaded; otherwise a still + play glyph (a video article awaiting its clip).
export function VideoPlayer({ title, len, uid }: { title: string; len: string; uid?: string }) {
  return (
    <div style={{ borderRadius: 3, overflow: "hidden", boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)" }}>
      {uid ? (
        <div style={{ position: "relative", height: 0, paddingTop: "56.25%", background: "#2d332e" }}>
          <iframe
            src={`https://iframe.videodelivery.net/${encodeURIComponent(uid)}`}
            title={title}
            loading="lazy"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          />
        </div>
      ) : (
        <div style={{ position: "relative", height: 300, background: "#2d332e", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ width: 58, height: 58, borderRadius: "50%", background: "rgba(255,255,255,.92)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="play" size={24} color="#1d241f" />
          </span>
        </div>
      )}
      <div style={{ background: "#1d241f", color: "#fff", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: "rgba(255,255,255,.7)" }}>{len}</span>
      </div>
    </div>
  );
}
